import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PopupRepository, PopupActionType } from 'repository/popupRepository';
import styles from './AppPopupModal.module.css';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

export interface AppPopupPage {
    title?: string;
    body?: string;
    imageUrl?: string;
}

export interface AppPopupData {
    popupId: number | string;
    title?: string;
    content?: string;     // JSON 약속: `{ "pages": [...] }`. 파싱 실패 시 단일 페이지 body 로 fallback.
    imageUrl?: string;
    confirmText?: string;
    showAgain?: string;   // 'Y' 면 "다시 안 보기" 체크박스 노출
}

interface Props {
    popup: AppPopupData;
    userId: string;
    onClose: () => void;
}

/**
 * 단일 팝업 + 멀티페이지 좌우 스와이프 모달.
 *
 * content JSON 약속:
 *   { "pages": [ { "title": "...", "body": "...", "imageUrl": "..." }, ... ] }
 * JSON 이 아니거나 pages 가 없으면 단일 페이지(popup.title/content/imageUrl) 로 렌더.
 *
 * 닫기 동작:
 *   - "다시 안 보기" 체크 후 닫음 → POST .../NEVER → 서버가 다음부터 이 사용자에게 자동 제외
 *   - 그 외 닫기 → POST .../CLOSE
 */
export const AppPopupModal: React.FC<Props> = ({ popup, userId, onClose }) => {
    const { t } = useTranslation();
    const [pageIdx, setPageIdx] = useState(0);
    const [neverChecked, setNeverChecked] = useState(false);
    const pagesRef = useRef<HTMLDivElement>(null);
    const viewSentRef = useRef(false);

    // content JSON 파싱 — 실패 시 단일 페이지로 fallback
    const pages: AppPopupPage[] = (() => {
        try {
            if (popup.content) {
                const parsed = JSON.parse(popup.content);
                if (parsed && Array.isArray(parsed.pages) && parsed.pages.length > 0) {
                    return parsed.pages.map((p: any) => ({
                        title: typeof p.title === 'string' ? p.title : undefined,
                        body: typeof p.body === 'string' ? p.body : undefined,
                        imageUrl: typeof p.imageUrl === 'string' ? p.imageUrl : undefined,
                    }));
                }
            }
        } catch { /* JSON 아님 — 단일 페이지로 */ }
        return [{ title: popup.title, body: popup.content, imageUrl: popup.imageUrl }];
    })();

    // 노출 기록 (1회만)
    useEffect(() => {
        if (viewSentRef.current) return;
        viewSentRef.current = true;
        PopupRepository.onPostAction(popup.popupId, 'VIEW', userId).catch(() => {});
    }, [popup.popupId, userId]);

    // 스크롤 위치로 현재 페이지 인덱스 추적
    const handleScroll = () => {
        const el = pagesRef.current;
        if (!el) return;
        const idx = Math.round(el.scrollLeft / el.clientWidth);
        if (idx !== pageIdx) setPageIdx(idx);
    };

    const closeWith = (action: PopupActionType) => {
        PopupRepository.onPostAction(popup.popupId, action, userId).catch(() => {});
        onClose();
    };

    const handleClose = () => {
        closeWith(neverChecked ? 'NEVER' : 'CLOSE');
    };

    const showAgainOption = popup.showAgain === 'Y';

    return (
        <div className={cls("apm-overlay")} onClick={handleClose}>
            <div className={cls("apm-card")} style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
                <button className={cls("apm-close-btn")} onClick={handleClose} aria-label={t('common.close')}>×</button>

                <div className={cls("apm-pages")} ref={pagesRef} onScroll={handleScroll}>
                    {pages.map((p, i) => (
                        <div key={i} className={cls("apm-page")}>
                            {p.imageUrl && <img className={cls("apm-page-img")} src={p.imageUrl} alt="" />}
                            {p.title && <h2 className={cls("apm-page-title")}>{p.title}</h2>}
                            {p.body && <div className={cls("apm-page-body")}>{p.body}</div>}
                        </div>
                    ))}
                </div>

                {pages.length > 1 && (
                    <div className={cls("apm-dots")}>
                        {pages.map((_, i) => (
                            <div key={i} className={cls(`apm-dot ${i === pageIdx ? 'active' : ''}`)} />
                        ))}
                    </div>
                )}

                <div className={cls("apm-footer")}>
                    {showAgainOption ? (
                        <label className={cls("apm-never-label")}>
                            <input
                                type="checkbox"
                                checked={neverChecked}
                                onChange={e => setNeverChecked(e.target.checked)}
                            />
                            <span>{t('common.dontShowAgain')}</span>
                        </label>
                    ) : <span />}
                    <button className={cls("apm-confirm-btn")} onClick={handleClose}>
                        {popup.confirmText || t('common.confirm')}
                    </button>
                </div>
            </div>
        </div>
    );
};
